<script context="module">
  import * as yup from "yup";

  export const forgottenPasswordSchema = yup.object().shape({
    email: yup
      .string()
      .required("validation.sign_in_form.email.required")
      .email("validation.sign_in_form.email.invalid")
  });
  export const signInSchema = yup.object().shape({
    email: yup
      .string()
      .required("validation.sign_in_form.email.required")
      .email("validation.sign_in_form.email.invalid"),
    password: yup
      .string()
      .required("validation.sign_in_form.password.required"),
  });
  export const registerSchema = yup.object().shape({
    name: yup
      .string()
      .required("validation.sign_in_form.name.required"),
    email: yup
      .string()
      .required("validation.sign_in_form.email.required")
      .email("validation.sign_in_form.email.invalid"),
    password: yup
      .string()
      .required("validation.sign_in_form.password.required")
      .min(6, "validation.sign_in_form.password.min")
  });

  export const FORGOTTEN_PASSWORD_MODE = "forgotten-password";
  export const PASSWORD_RESET_MODE = "password-reset";
  export const REGISTER_MODE = "register";
  export const SIGN_IN_MODE = "sign-in";
</script>

<script>
  import { createEventDispatcher, onDestroy } from "svelte";
  import Actions from "@components/Actions";
  import Button from "@components/Button";
  import FormField from "@components/FormField";
  import I18nHtml from "@components/I18nHtml";
  import TextButton from "@components/TextButton";
  import { _ } from "@services/i18n";
  import { currentUser } from "@stores/auth";
  import { showUserSheet } from "@stores/sheets";
  import { toSentenceCase } from "@utils/string";

  export let mode = REGISTER_MODE;
  export let title = $_("components.sign_in_form.register.title", {
    default: "Create an account"
  });

  $: switch(mode) {
    case FORGOTTEN_PASSWORD_MODE:
      title = $_("components.sign_in_form.forgotten_password.title", {
        default: "Reset your password"
      });
      break;
    case PASSWORD_RESET_MODE:
      title = $_("components.sign_in_form.password_reset.title", {
        default: "Please check your inbox"
      });
      break;
    case SIGN_IN_MODE:
      title = $_("components.sign_in_form.sign_in.title", {
        default: "Sign in"
      });
      break;
    default:
      title = $_("components.sign_in_form.register.title", {
        default: "Create an account"
      });
  }

  const dispatch = createEventDispatcher();
  const values = {};
  let errors = {};
  let unsubscriber;
  let working = false;

  /**
   * Switch form mode
   *
   * @param {string} newMode
   */
  function setMode(newMode) {
    mode = newMode;
  }

  /**
   * Dispatch close event
   */
  function close() {
    dispatch("close");
  }

  /**
   * Sign in user
   */
  async function signIn() {
    errors = {};
    working = true;
    try {
      await signInSchema.validate(values, {abortEarly: false});
      const { default: firebaseClient } = await import(
        /* webpackChunkName: "firebase" */
        "@services/firebase"
      );
      const { auth } = await firebaseClient();
      await auth.signInWithEmailAndPassword(
        values.email,
        values.password
      );
      showUserSheet.update(() => false);
      unsubscriber = currentUser.subscribe((value) => {
        if (value) {
          working = false;
          dispatch("success");
        }
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(`Sign in error: ${err}`);
      errors = handleErrors(err);
      working = false;
    }
  }

  /**
   * Register user
   */
  async function register() {
    errors = {};
    working = true;
    try {
      await registerSchema.validate(values, {abortEarly: false});
      const {
        name: displayName,
        email,
        password
      } = values;
      const { default: firebaseClient } = await import(
        /* webpackChunkName: "firebase" */
        "@services/firebase"
      );
      const { auth, firestore } = await firebaseClient();
      const {user} = await auth.createUserWithEmailAndPassword(email, password);
      await firestore.collection("users").doc(user.uid).set(
        { created: new Date().toISOString(), displayName, email },
        { merge: true }
      );
      showUserSheet.update(() => false);
      unsubscriber = currentUser.subscribe((value) => {
        if (value) {
          working = false;
          dispatch("success");
        }
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(`Sign in error: ${err}`);
      errors = handleErrors(err);
      working = false;
    }
  }

  /**
   * Send password reset email
   */
  async function resetPassword() {
    errors = {};
    working = true;
    try {
      const {email} = values;
      await forgottenPasswordSchema.validate({ email });
      const { default: firebaseClient } = await import(
        /* webpackChunkName: "firebase" */
        "@services/firebase"
      );
      const { auth } = await firebaseClient();
      await auth.sendPasswordResetEmail(email);
      setMode(PASSWORD_RESET_MODE);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(`Password reset error: ${err}`);
      errors = handleErrors(err);
    } finally {
      working = false;
    }
  }

  /**
   * Format errors
   *
   * @param {object} error
   * @returns {object}
   */
  function handleErrors(error) {
    if (error instanceof yup.ValidationError) {
      return error.inner.reduce(
        (fieldErrors, {message, path}) => (
          { ...fieldErrors, [path]: toSentenceCase(message) }
        ), {}
      );
    }

    import(
      /* webpackChunkName: "sentry-browser" */
      "@sentry/browser"
    ).then((Sentry) =>
      Sentry.captureException(error, {
        tags: {
          functionality: "SignInForm"
        }
      })
    );

    if (error.code) {
      return { form: "errors.incorrect_email_password" };
    }

    return { form: "errors.generic" };
  }

  onDestroy(() => {
    if (typeof unsubscriber === "function") {
      unsubscriber();
    }
  });
</script>

<style>
  .error,
  .forgotten-password,
  .terms-conditions {
    font-size: 1.4rem;
    margin: 0 0 1.6rem;
  }

  .error {
    color: var(--error-color);
  }

  fieldset {
    border: 0;
    margin: 0;
    padding: 0;
  }

  .left-actions {
    align-items: center;
    display: flex;
  }

  .left-actions > :not(:last-child) {
    margin: 0 1em 0 0;
  }

  .left-actions > :last-child {
    margin: 0;
  }
</style>

<form>
  {#if errors.form}
    <div class="error">{$_(errors.form, { default: "Something went wrong, please try again" })}</div>
  {/if}

  <fieldset disabled={working}>
    {#if mode === PASSWORD_RESET_MODE}
      <I18nHtml
        key="components.sign_in_form.password_reset.content.html"
        default={[
          // eslint-disable-next-line max-len
          "'<p>'We have sent an email with a link to reset your password to {email}.'</p>'",
          "'<p>'Click the link in the email to reset your password.'</p>'"
        ].join("")}
        email={values.email}
      />
    {:else if mode === REGISTER_MODE}
      <FormField
        bind:value={values.name}
        error={errors.name && $_(errors.name, { default: "Please enter your full name" })}
        label={
          $_("components.sign_in_form.fields.name.label", {
            default: "Full name"
          })
        }
        name="name"
        placeholder={
          $_("components.sign_in_form.fields.name.placeholder", {
            default: "Full name"
          })
        }
      />
    {/if}

    {#if mode !== PASSWORD_RESET_MODE}
      <FormField
        bind:value={values.email}
        error={
          errors.email &&
          $_(errors.email, {
            default: "{email} is not a valid email address",
            values: {
              email: values.email
            }
          })
        }
        label={
          $_("components.sign_in_form.fields.email.label", {
            default: "Email address"
          })
        }
        name="email"
        placeholder={
          $_("components.sign_in_form.fields.email.placeholder", {
            default: "Email address"
          })
        }
        type="email"
      />
    {/if}

    {#if [SIGN_IN_MODE, REGISTER_MODE].includes(mode)}
      <FormField
        bind:value={values.password}
        error={
          errors.password &&
          $_(errors.password, { default: "Please enter your password" })
        }
        label={
          $_("components.sign_in_form.fields.password.label", {
            default: "Password"
          })
        }
        name="password"
        placeholder={
          $_("components.sign_in_form.fields.password.placeholder", {
            default: "Password"
          })
        }
        type="password"
      />
    {/if}

    {#if mode === SIGN_IN_MODE}
      <p class="forgotten-password">
        <a
          href={"#"}
          title={
            $_("components.sign_in_form.sign_in.forgotten_password_link.title_attribute", {
              default: "Reset password"
            })
          }
          on:click|preventDefault={() => setMode(FORGOTTEN_PASSWORD_MODE)}
        >
          {
            $_("components.sign_in_form.sign_in.forgotten_password_link.text", {
            default: "Forgotten your password?"
            })
          }
        </a>
      </p>
    {:else if mode === REGISTER_MODE}
      <p class="terms-conditions">
        <I18nHtml
          key="components.sign_in_form.register.terms_and_conditions.html"
          default={[
            "By creating an account you accept our",
            `'<strong><a
                href="https://hereintheroom.com/terms-of-service/"
                target="_blank"
                title="terms of service"
              >'terms of service'</a></strong>'`,
            "and",
            `'<strong><a
                href="https://hereintheroom.com/privacy"
                target="_blank"
                title="privacy policy"
              >'privacy policy'</a></strong>'`
          ].join(" ")}
        />
      </p>
    {/if}

    <Actions>
      {#if mode === REGISTER_MODE}
        <div class="left-actions">
          <Button margin="0 1rem 0 0" on:click={register} {working}>
            {
              $_("components.sign_in_form.register.actions.primary.text", {
                default: "Create account"
              })
            }
          </Button>
          <p class="secondary-action">
            <I18nHtml
              key="components.sign_in_form.register.actions.secondary.html"
              on:click={() => setMode(SIGN_IN_MODE)}
              default={[
                "or ",
                `'<a
                    href="#"
                    title="sign in"
                    data-on="click|preventDefault"
                  >'sign in'</a>'`
              ].join(" ")}
            />
          </p>
        </div>
        <TextButton bold={true} on:click={close} size="1.6rem">
          {
            $_("components.sign_in_form.register.actions.tertiary.text", {
              default: "Close"
            })
          }
        </TextButton>
      {:else if mode === SIGN_IN_MODE}
        <div class="left-actions">
          <Button margin="0 1rem 0 0" on:click={signIn} {working}>
            {
              $_("components.sign_in_form.sign_in.actions.primary.text", {
                default: "Sign in"
              })
            }
          </Button>
          <p class="secondary-action">
            <I18nHtml
              key="components.sign_in_form.sign_in.actions.secondary.html"
              on:click={() => setMode(REGISTER_MODE)}
              default={[
                "or",
                `'<a
                    href="#"
                    title="create an account"
                    data-on="click|preventDefault"
                  >'create an account'</a>'`
              ].join(" ")}
            />
          </p>
        </div>
        <TextButton bold={true} on:click={close} size="1.6rem">
          {
            $_("components.sign_in_form.sign_in.actions.tertiary.text", {
              default: "Close"
            })
          }
        </TextButton>
      {:else if mode === FORGOTTEN_PASSWORD_MODE}
        <Button margin="0 1rem 0 0" on:click={resetPassword} {working}>
          {
            $_("components.sign_in_form.forgotten_password.actions.primary.text", {
              default: "Reset password"
            })
          }
        </Button>
        <TextButton bold={true} on:click={() => setMode(SIGN_IN_MODE)} size="1.6rem">
          {
            $_("components.sign_in_form.forgotten_password.actions.tertiary.text", {
              default: "Cancel"
            })
          }
        </TextButton>
      {/if}
    </Actions>
  </fieldset>
</form>
